<template>
  <modal-window :main-width="9">
    <h2>{{ isNew ? 'Add Category' : 'Edit Category' }}</h2>
    <p>{{ isNew ? 'Create a new category to organize your templates.' : 'Update the category details.' }}</p>

    <div class="form-group">
      <label>Name</label>
      <input type="text" class="form-control" v-model="form.name" placeholder="Category name" />
    </div>
    <div class="form-group">
      <label>Icon</label>
      <div class="icon-selector-container">
        <div
          class="icon-option"
          :class="{ selected: form.icon === '' }"
          @click="form.icon = ''"
          title="No Icon"
        >
          <i class="far fa-ban text-muted"></i>
        </div>
        <div
          v-for="icon in availableIcons"
          :key="icon.class"
          class="icon-option"
          :class="{ selected: form.icon === icon.class }"
          @click="form.icon = icon.class"
          :title="icon.name"
        >
          <i :class="icon.class"></i>
        </div>
      </div>
    </div>
    <template #sidebar>
      <div class="d-flex flex-column justify-content-end h-100">
        <div class="label mb-2">
          <button class="btn btn-block btn-sm btn-info" @click="close">
            <i class="far fa-times"></i>
            Cancel
          </button>
        </div>
        <div class="label mb-2">
          <button class="btn btn-block btn-sm btn-success" @click="saveAndClose">
            <i class="far fa-save"></i>
            Save and close
          </button>
        </div>
      </div>
    </template>
  </modal-window>
</template>

<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { TemplateCategoryDto } from '@/apiclient/apiclient_generated';
import { templateCategoryClient } from '@/apiclient/opsboard-api';
import { GlobalHelper } from '@/helpers/global-helper';
import ModalWindow from '@/components/ModalWindow.vue';

@Component({
  components: { ModalWindow }
})
export default class TemplateCategoryEditModal extends Vue {
  @Prop({ default: null }) category!: TemplateCategoryDto | null;
  @Prop({ required: true }) boardGuid!: string;
  @Prop({ default: 0 }) defaultSortOrder!: number;

  private form = {
    name: '',
    icon: '',
    sortOrder: 0,
  };

  private availableIcons = [
    { class: 'far fa-folder', name: 'Folder' },
    { class: 'far fa-folder-open', name: 'Folder Open' },
    { class: 'far fa-file', name: 'File' },
    { class: 'far fa-file-alt', name: 'File Alt' },
    { class: 'far fa-clipboard', name: 'Clipboard' },
    { class: 'far fa-clipboard-list', name: 'Clipboard List' },
    { class: 'far fa-tasks', name: 'Tasks' },
    { class: 'far fa-check-circle', name: 'Check Circle' },
    { class: 'far fa-check-square', name: 'Check Square' },
    { class: 'far fa-list', name: 'List' },
    { class: 'far fa-list-alt', name: 'List Alt' },
    { class: 'far fa-cog', name: 'Cog' },
    { class: 'far fa-cogs', name: 'Cogs' },
    { class: 'far fa-wrench', name: 'Wrench' },
    { class: 'far fa-tools', name: 'Tools' },
    { class: 'far fa-plane', name: 'Plane' },
    { class: 'far fa-plane-departure', name: 'Plane Departure' },
    { class: 'far fa-plane-arrival', name: 'Plane Arrival' },
    { class: 'far fa-helicopter', name: 'Helicopter' },
    { class: 'far fa-shipping-fast', name: 'Shipping Fast' },
    { class: 'far fa-truck', name: 'Truck' },
    { class: 'far fa-box', name: 'Box' },
    { class: 'far fa-boxes', name: 'Boxes' },
    { class: 'far fa-warehouse', name: 'Warehouse' },
    { class: 'far fa-building', name: 'Building' },
    { class: 'far fa-clock', name: 'Clock' },
    { class: 'far fa-calendar', name: 'Calendar' },
    { class: 'far fa-calendar-alt', name: 'Calendar Alt' },
    { class: 'far fa-calendar-check', name: 'Calendar Check' },
    { class: 'far fa-bell', name: 'Bell' },
    { class: 'far fa-envelope', name: 'Envelope' },
    { class: 'far fa-paper-plane', name: 'Paper Plane' },
    { class: 'far fa-comment', name: 'Comment' },
    { class: 'far fa-comments', name: 'Comments' },
    { class: 'far fa-user', name: 'User' },
    { class: 'far fa-users', name: 'Users' },
    { class: 'far fa-user-check', name: 'User Check' },
    { class: 'far fa-tag', name: 'Tag' },
    { class: 'far fa-tags', name: 'Tags' },
    { class: 'far fa-star', name: 'Star' },
    { class: 'far fa-flag', name: 'Flag' },
    { class: 'far fa-bookmark', name: 'Bookmark' },
    { class: 'far fa-heart', name: 'Heart' },
    { class: 'far fa-exclamation-circle', name: 'Exclamation Circle' },
    { class: 'far fa-exclamation-triangle', name: 'Exclamation Triangle' },
    { class: 'far fa-info-circle', name: 'Info Circle' },
    { class: 'far fa-question-circle', name: 'Question Circle' },
    { class: 'far fa-shield-alt', name: 'Shield' },
    { class: 'far fa-lock', name: 'Lock' },
    { class: 'far fa-key', name: 'Key' },
    { class: 'far fa-map', name: 'Map' },
    { class: 'far fa-map-marker-alt', name: 'Map Marker' },
    { class: 'far fa-globe', name: 'Globe' },
    { class: 'far fa-compass', name: 'Compass' },
    { class: 'far fa-route', name: 'Route' },
    { class: 'far fa-road', name: 'Road' },
    { class: 'far fa-gas-pump', name: 'Gas Pump' },
    { class: 'far fa-tachometer-alt', name: 'Tachometer' },
    { class: 'far fa-thermometer-half', name: 'Thermometer' },
    { class: 'far fa-wind', name: 'Wind' },
    { class: 'far fa-cloud', name: 'Cloud' },
    { class: 'far fa-cloud-sun', name: 'Cloud Sun' },
    { class: 'far fa-sun', name: 'Sun' },
    { class: 'far fa-moon', name: 'Moon' },
    { class: 'far fa-bolt', name: 'Bolt' },
    { class: 'far fa-fire', name: 'Fire' },
    { class: 'far fa-snowflake', name: 'Snowflake' },
    { class: 'far fa-anchor', name: 'Anchor' },
    { class: 'far fa-life-ring', name: 'Life Ring' },
    { class: 'far fa-medkit', name: 'Medkit' },
    { class: 'far fa-first-aid', name: 'First Aid' },
    { class: 'far fa-ambulance', name: 'Ambulance' },
    { class: 'far fa-heartbeat', name: 'Heartbeat' },
    { class: 'far fa-stethoscope', name: 'Stethoscope' },
    { class: 'far fa-phone', name: 'Phone' },
    { class: 'far fa-phone-alt', name: 'Phone Alt' },
    { class: 'far fa-mobile-alt', name: 'Mobile' },
    { class: 'far fa-laptop', name: 'Laptop' },
    { class: 'far fa-desktop', name: 'Desktop' },
    { class: 'far fa-server', name: 'Server' },
    { class: 'far fa-database', name: 'Database' },
    { class: 'far fa-hdd', name: 'Hard Drive' },
    { class: 'far fa-wifi', name: 'WiFi' },
    { class: 'far fa-signal', name: 'Signal' },
    { class: 'far fa-broadcast-tower', name: 'Broadcast Tower' },
    { class: 'far fa-satellite', name: 'Satellite' },
    { class: 'far fa-satellite-dish', name: 'Satellite Dish' },
    { class: 'far fa-radar', name: 'Radar' },
    { class: 'far fa-print', name: 'Print' },
    { class: 'far fa-fax', name: 'Fax' },
    { class: 'far fa-barcode', name: 'Barcode' },
    { class: 'far fa-qrcode', name: 'QR Code' },
    { class: 'far fa-chart-bar', name: 'Chart Bar' },
    { class: 'far fa-chart-line', name: 'Chart Line' },
    { class: 'far fa-chart-pie', name: 'Chart Pie' },
    { class: 'far fa-chart-area', name: 'Chart Area' },
    { class: 'far fa-percentage', name: 'Percentage' },
    { class: 'far fa-calculator', name: 'Calculator' },
    { class: 'far fa-coins', name: 'Coins' },
    { class: 'far fa-money-bill', name: 'Money Bill' },
    { class: 'far fa-credit-card', name: 'Credit Card' },
    { class: 'far fa-receipt', name: 'Receipt' },
    { class: 'far fa-file-invoice', name: 'File Invoice' },
    { class: 'far fa-file-contract', name: 'File Contract' },
    { class: 'far fa-file-signature', name: 'File Signature' },
    { class: 'far fa-pen', name: 'Pen' },
    { class: 'far fa-pencil-alt', name: 'Pencil' },
    { class: 'far fa-edit', name: 'Edit' },
    { class: 'far fa-eraser', name: 'Eraser' },
    { class: 'far fa-highlighter', name: 'Highlighter' },
    { class: 'far fa-paperclip', name: 'Paperclip' },
    { class: 'far fa-thumbtack', name: 'Thumbtack' },
    { class: 'far fa-link', name: 'Link' },
    { class: 'far fa-unlink', name: 'Unlink' },
    { class: 'far fa-external-link-alt', name: 'External Link' },
    { class: 'far fa-share', name: 'Share' },
    { class: 'far fa-share-alt', name: 'Share Alt' },
  ];

  get isNew(): boolean {
    return !this.category;
  }

  mounted() {
    this.resetForm();
  }

  @Watch('category')
  onCategoryChange() {
    this.resetForm();
  }

  private resetForm() {
    if (this.category) {
      this.form.name = this.category.name || '';
      this.form.icon = this.category.icon || '';
      this.form.sortOrder = this.category.sortOrder || 0;
    } else {
      this.form.name = '';
      this.form.icon = '';
      this.form.sortOrder = this.defaultSortOrder;
    }
  }

  close() {
    this.$root.$bvModal.hide('categoryEditModal');
  }

  async saveAndClose() {
    if (!this.form.name) {
      GlobalHelper.NotifyError('Category name is required');
      return;
    }

    try {
      const dto = new TemplateCategoryDto({
        guid: this.category?.guid,
        boardGuid: this.boardGuid,
        name: this.form.name,
        icon: this.form.icon,
        sortOrder: this.form.sortOrder,
      });

      if (this.isNew) {
        await templateCategoryClient.create(dto);
        GlobalHelper.NotifySuccess('Category created');
      } else {
        await templateCategoryClient.update(dto.guid!, dto);
        GlobalHelper.NotifySuccess('Category updated');
      }

      this.close();
    } catch (error) {
      GlobalHelper.NotifyError('Failed to save category');
    }
  }
}
</script>

<style scoped lang="scss">
.icon-selector-container {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 8px;
  background: #fff;
}

.icon-option {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background-color: #e9ecef;
    border-color: #dee2e6;
  }

  &.selected {
    background-color: #007bff;
    border-color: #007bff;
    color: #fff;

    i {
      color: #fff;
    }
  }

  i {
    font-size: 1.1rem;
  }
}
</style>
